import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../lib/queryClient';
import * as dashboardService from '../services/dashboardService';

// Dashboard overview query
export const useDashboard = () => {
  return useQuery({
    queryKey: queryKeys.dashboard.overview(),
    queryFn: () => dashboardService.getDashboardOverview(),
    refetchInterval: 60000, // Refetch every minute
    staleTime: 30000, // Consider data stale after 30 seconds
  });
};

// Dashboard statistics query
export const useDashboardStats = (params?: {
  period?: 'today' | 'week' | 'month' | 'custom';
  start_date?: string;
  end_date?: string;
  school_id?: string;
}) => {
  return useQuery({
    queryKey: queryKeys.dashboard.stats(params),
    queryFn: () => dashboardService.getDashboardStats(params),
    refetchInterval: 300000, // Refetch every 5 minutes
    staleTime: 120000, // Consider data stale after 2 minutes
  });
};

// Activity feed query with pagination support
export const useActivityFeed = (params?: {
  limit?: number;
  cursor?: string;
  types?: string[];
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  start_date?: string;
  end_date?: string;
}) => {
  return useQuery({
    queryKey: queryKeys.dashboard.activityFeed(params),
    queryFn: () => dashboardService.getActivityFeed(params),
    keepPreviousData: true, // Keep previous data while fetching new page
    refetchInterval: 30000, // Refetch every 30 seconds
  });
};

// Real-time metrics query for dashboard
export const useRealTimeMetrics = () => {
  return useQuery({
    queryKey: ['dashboard', 'realtime'],
    queryFn: () => dashboardService.getRealTimeMetrics(),
    refetchInterval: 30000, // 30 seconds
    staleTime: 15000, // 15 seconds
  });
};

// Combined dashboard data hook for convenience
export const useDashboardData = (options?: {
  includeStats?: boolean;
  statsPeriod?: 'today' | 'week' | 'month';
  schoolId?: string;
}) => {
  const overview = useDashboard();
  const stats = useDashboardStats(
    options?.includeStats !== false
      ? {
          period: options?.statsPeriod || 'today',
          school_id: options?.schoolId
        }
      : undefined
  );

  return {
    overview: overview.data,
    stats: stats.data,
    isLoading: overview.isLoading || (options?.includeStats !== false && stats.isLoading),
    error: overview.error || stats.error,
    refetch: () => {
      overview.refetch();
      if (options?.includeStats !== false) {
        stats.refetch();
      }
    }
  };
};

// Real-time metrics hook for critical monitoring
export const useRealtimeMetrics = () => {
  const dashboard = useDashboard();
  const activityFeed = useActivityFeed({
    limit: 10,
    priority: 'high'
  });

  return {
    systemStatus: dashboard.data && dashboard.data.system_status,
    unresolved_alerts: (dashboard.data && dashboard.data.summary && dashboard.data.summary.unresolved_alerts) || 0,
    recent_alerts: (dashboard.data && dashboard.data.recent_alerts) || [],
    high_priority_activities: (activityFeed.data && activityFeed.data.activities) || [],
    isHealthy: dashboard.data && dashboard.data.system_status && dashboard.data.system_status.status === 'healthy',
    isLoading: dashboard.isLoading || activityFeed.isLoading,
  };
};
